/* eslint-disable max-lines-per-function, complexity, max-lines */
/* Cohesive single-file section component — see project guidelines. */
import { motion } from "framer-motion";
import { IMAGES } from "@/data/site";
import { Play, ArrowRight, Sparkles } from "lucide-react";

const sparkles = Array.from({ length: 18 }).map((_, i) => ({
    id: i,
    top: Math.random() * 90 + 5,
    left: Math.random() * 95 + 2,
    size: Math.random() * 3 + 1,
    delay: Math.random() * 4,
}));

export const Hero = ({ onApply, onPartner, onWatch }) => {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-screen overflow-hidden flex items-center"
        >
            {/* Background image with slow zoom */}
            <motion.div
                initial={{ scale: 1 }}
                animate={{ scale: 1.08 }}
                transition={{ duration: 14, ease: "easeOut" }}
                className="absolute inset-0"
            >
                <img
                    src={IMAGES.heroBg}
                    alt="Young Mogul Program hero"
                    className="w-full h-full object-cover"
                />
            </motion.div>

            {/* Dark gradient overlays */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050505]" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />

            {/* Floating gold sparkles */}
            <div className="absolute inset-0 pointer-events-none">
                {sparkles.map((s) => (
                    <div
                        key={s.id}
                        className="absolute rounded-full bg-[#FFDF73] animate-pulse-gold"
                        style={{
                            top: `${s.top}%`,
                            left: `${s.left}%`,
                            width: `${s.size}px`,
                            height: `${s.size}px`,
                            boxShadow: "0 0 12px #FFDF73, 0 0 24px #D4AF37",
                            animationDelay: `${s.delay}s`,
                        }}
                    />
                ))}
                <div className="absolute top-1/4 -left-32 w-[400px] h-[400px] rounded-full bg-[#D4AF37]/10 blur-3xl animate-float-slow" />
                <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] rounded-full bg-[#FFDF73]/5 blur-3xl animate-float-slower" />
            </div>

            {/* Vertical side caption — only on truly wide screens (2xl = 1536px+)
                where the centered content leaves enough left gutter. Uses
                writing-mode (not rotate-90) so its layout width is the text's
                height (~14px), not the unrotated text width (~220px). */}
            <div className="hidden 2xl:flex absolute left-6 top-1/2 -translate-y-1/2 flex-col items-center gap-6 z-10 pointer-events-none">
                <div className="h-24 w-px bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent" />
                <span
                    className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37] whitespace-nowrap"
                    style={{ writingMode: "vertical-rl" }}
                >
                    EST. Sacramento 2017
                </span>
                <div className="h-24 w-px bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-40 pb-20 sm:pt-36 sm:pb-24 lg:py-32 w-full">
                <div className="grid lg:grid-cols-12 lg:gap-6 xl:gap-10 items-end">
                    <div className="lg:col-span-8">
                {/* Mobile / tablet / small-desktop — horizontal EST badge.
                    Shown on everything below 2xl since the vertical side
                    caption only appears on 2xl+ where the gutter is wide
                    enough to avoid overlap. */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.25 }}
                    className="2xl:hidden flex items-center gap-3 mb-5"
                    data-testid="hero-est-badge"
                >
                    <span className="h-px w-8 bg-[#D4AF37]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] whitespace-nowrap">
                        EST · Sacramento · 2017
                    </span>
                    <span className="h-px flex-1 max-w-12 bg-gradient-to-r from-[#D4AF37] to-transparent" />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.35 }}
                    className="max-w-5xl"
                    data-testid="hero-headline"
                >
                    <span className="font-display animate-shimmer text-5xl sm:text-6xl lg:text-8xl uppercase tracking-tight leading-none block">
                        Young Moguls
                    </span>
                    <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.92] tracking-tighter mt-2">
                        Dreams Come True
                    </h1>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.55 }}
                    className="mt-5"
                >
                    <span className="inline-block bg-[#D4AF37] text-black font-display text-xl sm:text-2xl lg:text-4xl uppercase leading-none tracking-tight px-5 py-3 sm:px-6 sm:py-4 border border-[#FFDF73] shadow-[0_0_30px_rgba(212,175,55,0.35)]">
                        When You Master Your Own Destiny
                    </span>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.75 }}
                    className="mt-6 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed"
                >
                    A 9-week Digital Media, Marketing, and Mentorship
                    Accelerator empowering the next generation of creators,
                    leaders, and entrepreneurs.
                </motion.p>

                {/* Mobile / tablet hero photo (hidden on lg+ since the right
                    column shows the same image there) */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.85 }}
                    className="lg:hidden relative mt-8"
                    data-testid="hero-image-mobile"
                >
                    <div className="relative aspect-[4/3] sm:aspect-video overflow-hidden border border-[#D4AF37]/40 gold-glow">
                        <img
                            src="/assets/hero-amp-stage.jpg"
                            alt="Young Moguls performing on the AMP stage"
                            loading="eager"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
                    </div>
                    <span className="absolute -top-2 -left-2 h-5 w-5 border-t-2 border-l-2 border-[#D4AF37]" />
                    <span className="absolute -bottom-2 -right-2 h-5 w-5 border-b-2 border-r-2 border-[#D4AF37]" />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.95 }}
                    className="mt-10 flex flex-wrap gap-4"
                >
                    <button
                        data-testid="hero-apply-btn"
                        onClick={onApply}
                        className="group bg-[#D4AF37] text-black font-display uppercase tracking-widest px-8 py-4 hover:bg-[#FFF3A1] transition-all hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] flex items-center gap-3"
                    >
                        Apply Now
                        <ArrowRight
                            size={18}
                            className="group-hover:translate-x-1 transition-transform"
                        />
                    </button>
                    <button
                        data-testid="hero-partner-btn"
                        onClick={onPartner}
                        className="group border border-white/30 text-white px-8 py-4 font-display uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all flex items-center gap-3"
                    >
                        Partner With Us
                        <ArrowRight
                            size={18}
                            className="group-hover:translate-x-1 transition-transform"
                        />
                    </button>
                    <button
                        data-testid="hero-watch-btn"
                        onClick={onWatch}
                        className="group flex items-center gap-3 text-white/80 hover:text-[#D4AF37] transition-colors font-display uppercase tracking-widest px-2 py-4"
                    >
                        <span className="h-12 w-12 rounded-full border border-[#D4AF37] grid place-items-center group-hover:bg-[#D4AF37] transition-colors">
                            <Play
                                size={16}
                                className="text-[#D4AF37] group-hover:text-black ml-0.5"
                            />
                        </span>
                        Watch The Promo
                    </button>
                </motion.div>

                    </div>

                    {/* Right — hero photo */}
                    {/* Right — hero photo (outer div owns the static scale/origin
                        so it isn't fought by framer-motion's inline transform) */}
                    <div className="lg:col-span-4 hidden lg:block relative lg:-translate-x-1 xl:-translate-x-1 lg:scale-[1.2] origin-bottom-left">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.1, delay: 0.5 }}
                        className="relative"
                        data-testid="hero-image"
                    >
                        <div className="relative aspect-square overflow-hidden border border-[#D4AF37]/40 gold-glow">
                            <img
                                src="/assets/hero-amp-stage.jpg"
                                alt="Young Moguls performing on the AMP stage"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                            <div className="absolute inset-0 ring-1 ring-inset ring-[#D4AF37]/10 pointer-events-none" />
                        </div>

                        {/* Corner accents */}
                        <span className="absolute -top-2 -left-2 h-6 w-6 border-t-2 border-l-2 border-[#D4AF37]" />
                        <span className="absolute -bottom-2 -right-2 h-6 w-6 border-b-2 border-r-2 border-[#D4AF37]" />
                    </motion.div>
                    </div>
                </div>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.15 }}
                    className="mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-[#D4AF37]/20 border border-[#D4AF37]/20 max-w-4xl"
                >
                    {[
                        ["400+", "Young Moguls"],
                        ["3.6K+", "Students Reached"],
                        ["7+", "Districts"],
                        ["45+", "Community Events"],
                        ["9 WK", "Accelerator"],
                    ].map(([n, l]) => (
                        <div
                            key={l}
                            className="bg-black/80 backdrop-blur-md p-4 sm:p-5"
                        >
                            <div className="font-accent text-3xl sm:text-4xl text-[#D4AF37] leading-none">
                                {n}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-2">
                                {l}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>

            {/* Bottom fade to next section */}
            <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-[#050505] to-transparent z-10" />
        </section>
    );
};
